/**
 * Search-indexing policy for `/research/supplements/[name]/` entity pages.
 *
 * Articles can be tagged with `supplement_key` for editorial grouping, but not
 * every key represents an ingredient that deserves a supplement landing page.
 * Keep this policy centralized so sitemap, metadata, browse links, and audits
 * agree on what crawlers should treat as an indexable supplement entity.
 */

export const MIN_INDEXABLE_SUPPLEMENT_ARTICLES = 1;

const EXPLICIT_NON_SUPPLEMENT_KEYS = new Set([
  'apob',
  'alzheimer',
  'alzheimers',
  'alzheimers_disease',
  'alzheimers_prevention',
  'brain_biomarkers',
  'cortisol',
  'crp',
  'cognitive_reserve',
  'cognitive_risk',
  'cognitive_risk_score',
  'dementia',
  'drug_supplement_interactions',
  'epigenetic_clocks',
  'gfap',
  'gut_microbiome',
  'hba1c',
  'homocysteine',
  'hpa_axis',
  'hs_crp',
  'insulin_resistance_spectrum',
  'interactions',
  'ldl_cholesterol',
  'mineralocorticoid_glucocorticoid_receptor_balance',
  'omega_3_index',
  'ptau217',
  'p_tau217',
  'proofmark',
  'proofmark_scoring',
  'skin_hair_collagen',
  'supplement_scanning',
  'testosterone',
  'zonulin',
]);

const NON_SUPPLEMENT_PATTERNS = [
  /(^|_)biomarkers?($|_)/,
  /(^|_)markers($|_)/,
  /(^|_)health($|_)/,
  /(^|_)longevity($|_)/,
  /(^|_)recovery($|_)/,
  /(^|_)alzheimers?($|_)/,
  /(^|_)dementia($|_)/,
  /(^|_)blood_tests?($|_)/,
  /(^|_)cognitive_performance($|_)/,
  /(^|_)system_health($|_)/,
  /(^|_)perimenopausal($|_)/,
  /_guide$/,
  /_protocol$/,
  /_risk_score$/,
  /_101$/,
];

export function normalizeSupplementKey(key: string): string {
  return key.trim().toLowerCase().replace(/-/g, '_');
}

export function getNonSupplementEntityReason(key: string): string | null {
  const normalized = normalizeSupplementKey(key);

  if (EXPLICIT_NON_SUPPLEMENT_KEYS.has(normalized)) {
    return 'biomarker or editorial topic used as supplement_key';
  }

  if (NON_SUPPLEMENT_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return 'editorial guide or taxonomy placeholder';
  }

  return null;
}

export function isNonSupplementEntityKey(key: string): boolean {
  return getNonSupplementEntityReason(key) !== null;
}

export function isIngredientLikeSupplementKey(key: string): boolean {
  return !isNonSupplementEntityKey(key);
}

export function isIndexableSupplementPage(key: string, articleCount: number): boolean {
  return (
    isIngredientLikeSupplementKey(key) &&
    articleCount >= MIN_INDEXABLE_SUPPLEMENT_ARTICLES
  );
}
